import {
    DataTable,
    TikTokTableCol,
    TikTokTableHeadColFilter,
} from 'components/DataVis/DataTable';
import {
    defaultFilterAccessorsAndOptions,
    defaultHandleFilter,
    useCardsFilterSort,
} from 'components/CardsWrapper';
import {
    useAreFlavorFiltersSupported,
    useEndDate,
    useHandleAreFlavorFiltersSupported,
    useIsAdmin,
    useResetSortOptionFlavorUnsupported,
    useSetupFlavorFilters,
} from 'utils/hooks';
import { useCallback, useState } from 'react';
import CatFourFilterList from 'components/FlavorFilters/CatFourFilterList';
import DownloadCSVMenuButton from 'components/Button/DownloadCSVMenuButton';
import ErrorBoundary from 'containers/ErrorBoundary';
import { FLAVORS_METADATA } from 'components/FlavorFilters/utils';
import FilterSelect from 'components/Input/FilterSelect';
import { SectionCard } from 'components/SectionCard';
import Stack from 'components/Stack';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import { getOnChange } from 'components/SectionTableFilterChips';
import useHashtagDeepLink from 'utils/hooks/useHashtagDeepLink';
import useTableColumns from 'utils/hooks/useTableColumns';

const filterFlavorsColumn = (columns) =>
    columns?.filter((key) => !FLAVORS_METADATA.key.includes(key));

function createCSVDownloadOptions({
    source,
    name,
    country,
    date,
    isAdmin,
    sortedData,
    paginateBy,
    brands,
    categories,
    hashtags,
    visibleOrderedMetrics,
    getDownloadOptions,
}) {
    return getDownloadOptions({
        entity: name || 'all-industries',
        country,
        date,
        source,
        sortedData,
        paginateBy,
        brand: brands || null,
        category: categories || null,
        hashtag: hashtags || null,
        visibleOrderedMetrics,
        isAdmin,
    });
}

function ExploreDataSourceTable({
    isLoading,
    dataQuery,
    category,
    categoryQuery,
    categoryId,
    country,
    hashtags,
    brands,
    categories,
    week,
    year,
    trendDimension,
    paginateBy,
    name,
    tableName,
    NameBodyColComponent,
    hashtagHealthStatFilterOptions,
    healthStatFilterOptions,
    getDownloadOptions,
    source,
    mixpanelMeta,
    filterSelectMenus,
    defaultInitialSortOption,
    PresetFilterChipsComponent,
    DrawerComponent,
    DrawerContentDefaultComponent,
    DrawerContentHashtagComponent,
    filterColumns,
    sortOptions,
    metrics,
    noDataMessageOf,
    rowHeight: rowHeightOverride,
}) {
    const isAdmin = useIsAdmin();
    const date = useEndDate(source);
    const isHashtagData = !!hashtags;
    const flavorFiltersSupported = useAreFlavorFiltersSupported({
        categoryQuery,
    });
    const areFlavorFiltersSupported =
        isHashtagData || brands ? false : flavorFiltersSupported;

    const {
        handleFilter,
        filterAccessorsAndOptions,
        handleFiltersWithoutCatFours,
    } = useSetupFlavorFilters({
        defaultHandleFilter,
        defaultFilterAccessorsAndOptions,
    });

    const isDataLoaded = !(
        dataQuery.isLoading ||
        !dataQuery.data ||
        (dataQuery.data &&
            dataQuery.data.some(
                ({ fetching, fetchingData }) => fetching || fetchingData,
            ))
    );
    const filterMissingData = false;
    const {
        filters,
        filtersKey,
        onSearchInputChange,
        setFilter,
        setFilters,
        resetFilters,
        initialSortOption,
        sortedData,
        sortOption,
        sortOptionKey,
        setSortOption: baseSetSortOption,
    } = useCardsFilterSort({
        rootCategoryId: categoryId,
        data: dataQuery.data,
        sortOptions,
        initialSortOption: defaultInitialSortOption,
        filterMissingData,
        handleFilter,
        filterAccessorsAndOptions,
    });

    const { drawerProps, linkedHashtag } = useHashtagDeepLink({
        enabled: isHashtagData,
        rows: sortedData,
    });

    useHandleAreFlavorFiltersSupported({ categoryQuery, filters, setFilters });

    const compositeFilterColumns = (columns) => {
        let filteredColumns = columns;

        if (typeof filterColumns === 'function') {
            filteredColumns = filterColumns(filteredColumns);
        }

        if (!areFlavorFiltersSupported) {
            filteredColumns = filterFlavorsColumn(filteredColumns);
        }

        return filteredColumns;
    };
    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName,
            metrics,
            setSortOption: baseSetSortOption,
            filterColumns: compositeFilterColumns,
        });

    const setSupportedSortOption = ({ sortBy, ascending }) =>
        setSortOption(
            sortOptions.find(
                ({ sortBy: a, ascending: b }) =>
                    sortBy === a && ascending === b,
            ),
        );

    useResetSortOptionFlavorUnsupported({
        categoryQuery,
        areFlavorFiltersSupported,
        sortOptionKey,
        setSupportedSortOption,
        defaultInitialSortOption,
    });

    const noDataMessage = noDataMessageOf
        ? noDataMessageOf()
        : dataQuery.data && dataQuery.data.length > 0
          ? `No ${trendDimension} available. Try adjusting the filters.`
          : `No ${trendDimension} available.`;

    const visibleColumnsConfig = createDefaultColumns(
        ['name', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );

    if (NameBodyColComponent) {
        visibleColumnsConfig[0].formatCell = ({ row }) => (
            <NameBodyColComponent
                row={row}
                isHashtagData={isHashtagData}
                type={trendDimension}
            />
        );
    }

    const healthStatOptions = isHashtagData
        ? hashtagHealthStatFilterOptions
        : healthStatFilterOptions;

    const downloadOptions = createCSVDownloadOptions({
        source,
        name,
        country,
        date,
        isAdmin,
        sortedData,
        paginateBy,
        brands,
        categories,
        hashtags,
        visibleOrderedMetrics,
        getDownloadOptions,
    });

    const rowHeight =
        rowHeightOverride ??
        (!isHashtagData || trendDimension === 'brands' ? 75 : 50);
    const dataReady =
        !isLoading && sortedData && (sortedData.length > 0 || isDataLoaded);

    const getTableData = () => {
        if (sortedData && sortedData.length > 0) {
            return sortedData;
        }
        if (isLoading) {
            return null;
        }
        return sortedData;
    };

    const renderDrawerContent = (selectedId) => {
        const hashtagName = linkedHashtag(selectedId);
        if (hashtagName) {
            // Try to find the data in sortedData (may or may not exist)
            const dataPoint = sortedData?.find(
                (row) => row.name === hashtagName,
            );

            return (
                <ErrorBoundary>
                    <DrawerContentHashtagComponent
                        hashtag={hashtagName}
                        category={categories?.[0] || null}
                        brand={
                            dataPoint ? dataPoint.brand : brands ? '*' : null
                        }
                        initialData={dataPoint || undefined}
                    />
                </ErrorBoundary>
            );
        }

        // Normal case - row clicked in table
        const dataPoint =
            selectedId &&
            sortedData &&
            sortedData.find(({ id }) => id === selectedId);
        if (!dataPoint) return null;
        return (
            <ErrorBoundary>
                {isHashtagData ? (
                    <DrawerContentHashtagComponent
                        hashtag={dataPoint.name}
                        initialData={dataPoint}
                        category={dataPoint.category}
                        brand={dataPoint.brand || (brands ? '*' : null)}
                    />
                ) : (
                    <DrawerContentDefaultComponent
                        paginateBy={paginateBy}
                        dataPoint={dataPoint}
                        parentHierarchies={category?.hierarchies}
                    />
                )}
            </ErrorBoundary>
        );
    };

    let menus = [...filterSelectMenus];
    if (areFlavorFiltersSupported) menus.push('flavors');
    else if (!areFlavorFiltersSupported)
        menus = menus.filter((menu) => menu !== 'flavors');

    return (
        <Stack $space=".5rem">
            <SectionCard css="padding-top: 1.25rem;">
                <Stack $space="1.5rem">
                    <Stack
                        $space=".75rem"
                        $direction="row"
                        $wrap={false}
                        css="align-items: flex-start;"
                    >
                        <PresetFilterChipsComponent
                            filters={filters}
                            setFilters={setFilters}
                            sortOption={sortOption}
                            setSortOption={setSupportedSortOption}
                            initialSortOption={initialSortOption}
                            healthStatOptions={healthStatOptions}
                            week={week}
                            year={year}
                        >
                            <FilterSelect
                                filters={filters}
                                setFilter={setFilter}
                                setFilters={setFilters}
                                resetFilters={resetFilters}
                                sortOption={sortOption}
                                setSortOption={setSupportedSortOption}
                                initialSortOption={initialSortOption}
                                mixpanelMeta={mixpanelMeta}
                                menus={menus}
                                getOnChange={getOnChange}
                                showAllHealthStats={true}
                                showPresetsMenu={false}
                                healthStatFilterOptions={healthStatOptions}
                                menuAlign="left"
                                css="align-self: flex-start;"
                                data={dataQuery.data}
                                isLoadingData={isLoading}
                                filterMissingData={filterMissingData}
                            />
                        </PresetFilterChipsComponent>
                        <Stack
                            $space=".75rem"
                            $direction="row"
                            css="display: flex; flex-shrink: 0; margin-left: auto;"
                        >
                            <DownloadCSVMenuButton
                                isDisabled={!dataReady}
                                isLoading={isLoading}
                                options={downloadOptions}
                                mixpanelMeta={mixpanelMeta}
                                buttonProps={{ size: 'default' }}
                                css="align-self: flex-start; margin-left: auto;"
                            />
                            {columnsMenu}
                        </Stack>
                    </Stack>
                    <CatFourFilterList
                        filters={filters}
                        setFilter={setFilter}
                        isLoading={isLoading}
                        data={dataQuery.data}
                        filterAccessorsAndOptions={filterAccessorsAndOptions}
                        handleFiltersWithoutCatFours={
                            handleFiltersWithoutCatFours
                        }
                        $placeholderMargin={'0 0 1.5rem 0'}
                    />
                    <DataTable
                        data={getTableData()}
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        week={week}
                        year={year}
                        renderDrawerContent={renderDrawerContent}
                        {...drawerProps}
                        columns={visibleColumnsConfig}
                        sortOption={sortOption}
                        setSortOption={setSupportedSortOption}
                        onSearchInputChange={onSearchInputChange}
                        name={name}
                        rowHeight={rowHeight}
                        noDataMessage={noDataMessage}
                        DrawerComponent={DrawerComponent}
                        TableCol={TikTokTableCol}
                        TableHeadColFilter={TikTokTableHeadColFilter}
                    />
                </Stack>
            </SectionCard>
        </Stack>
    );
}

export function ExploreDataSourceTableStarter({
    isLoading,
    dataQuery,
    category,
    country,
    hashtags,
    brands,
    categories,
    week,
    year,
    trendDimension,
    paginateBy,
    name,
    tableName,
    NameBodyColComponent,
    getDownloadOptions,
    source,
    mixpanelMeta,
    defaultInitialSortOption,
    PresetFilterChipsComponent,
    DrawerComponent,
    DrawerContentDefaultComponent,
    DrawerContentHashtagComponent,
    ProFeatureModalComponent,
    ProPaywallComponent,
    filterColumns,
    metrics,
}) {
    const isAdmin = useIsAdmin();
    const [filterAndSortModalIsOpen, setFilterAndSortModalIsOpen] =
        useState(false);
    const date = useEndDate(source);
    const sortedData = dataQuery.data;
    const isHashtagData = !!hashtags;
    const { visibleOrderedMetrics, columnsMenu } = useTableColumns({
        tableName,
        metrics,
        filterColumns,
    });

    const setSupportedSortOption = () => setFilterAndSortModalIsOpen(true);
    const onSearchInputChange = useCallback(
        (input) => {
            if (input) setFilterAndSortModalIsOpen(true);
        },
        [setFilterAndSortModalIsOpen],
    );

    const noDataMessage = `No ${trendDimension} available.`;

    const visibleColumnsConfig = createDefaultColumns(
        ['name', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );

    visibleColumnsConfig[0].formatCell = ({ row }) => (
        <NameBodyColComponent
            row={row}
            isHashtagData={isHashtagData}
            type={trendDimension}
        />
    );

    const downloadOptions = createCSVDownloadOptions({
        source,
        name,
        country,
        date,
        isAdmin,
        sortedData,
        paginateBy,
        brands,
        categories,
        hashtags,
        visibleOrderedMetrics,
        getDownloadOptions,
    });

    const rowHeight = !isHashtagData || trendDimension === 'brands' ? 75 : 50;
    const dataReady = !isLoading && sortedData && sortedData.length > 0;
    const renderDrawerContent = (selectedId) => {
        const dataPoint =
            selectedId &&
            sortedData &&
            sortedData.find(({ id }) => id === selectedId);
        if (!dataPoint) return null;
        return (
            <ErrorBoundary>
                {isHashtagData ? (
                    <DrawerContentHashtagComponent
                        hashtag={dataPoint.name}
                        initialData={dataPoint}
                        category={dataPoint.category}
                        brand={dataPoint.brand}
                    />
                ) : (
                    <DrawerContentDefaultComponent
                        paginateBy={paginateBy}
                        dataPoint={dataPoint}
                        parentHierarchies={category?.hierarchies}
                    />
                )}
            </ErrorBoundary>
        );
    };

    return (
        <SectionCard css="padding-top: 1.25rem;">
            <Stack $space="1.5rem">
                <Stack
                    $space=".75rem"
                    $direction="row"
                    $wrap={false}
                    css="align-items: flex-start;"
                >
                    <PresetFilterChipsComponent
                        setFilters={setSupportedSortOption}
                    />
                    <Stack
                        $space=".75rem"
                        $direction="row"
                        css="display: flex; flex-shrink: 0; margin-left: auto;"
                    >
                        <DownloadCSVMenuButton
                            isDisabled={!dataReady}
                            isLoading={isLoading}
                            options={downloadOptions}
                            mixpanelMeta={mixpanelMeta}
                            buttonProps={{ size: 'default' }}
                            css="align-self: flex-start; margin-left: auto;"
                        />
                        {columnsMenu}
                    </Stack>
                </Stack>
                <DataTable
                    data={
                        sortedData && sortedData.length > 0
                            ? sortedData
                            : isLoading
                              ? null
                              : sortedData
                    }
                    week={week}
                    year={year}
                    renderDrawerContent={renderDrawerContent}
                    columns={visibleColumnsConfig}
                    sortOption={defaultInitialSortOption}
                    setSortOption={setSupportedSortOption}
                    onSearchInputChange={onSearchInputChange}
                    name={name}
                    rowHeight={rowHeight}
                    noDataMessage={noDataMessage}
                    DrawerComponent={DrawerComponent}
                    TableCol={TikTokTableCol}
                    TableHeadColFilter={TikTokTableHeadColFilter}
                />
                {sortedData && sortedData.length > 19 ? (
                    <ProPaywallComponent />
                ) : null}
            </Stack>
            <ProFeatureModalComponent
                open={!!filterAndSortModalIsOpen}
                onClose={() => setFilterAndSortModalIsOpen(false)}
            />
        </SectionCard>
    );
}

export default ExploreDataSourceTable;
